import { Fragment, useEffect, useRef } from "react";
import { Move3d, Sun } from "lucide-react";

const PANEL_W = 70;
const PANEL_LEN = 150;
const N = 6;
const GAP = 8;
const PX_PER_FT = 20;

export const Row3D = ({ tilt, shadowFt, shadowSafe }) => {
  const worldRef = useRef(null);
  const sunRef = useRef(null);
  const yawRef = useRef(-18);
  const rxRef = useRef(-58);
  const dragRef = useRef({ active: false, x: 0, y: 0, idle: 0 });

  useEffect(() => {
    let raf;
    const apply = () => {
      if (worldRef.current) {
        worldRef.current.style.transform = `rotateX(${rxRef.current}deg) rotateY(${yawRef.current}deg)`;
      }
      if (sunRef.current) {
        sunRef.current.style.transform = `translate(-50%, -50%) translate3d(130px, -180px, -300px) rotateY(${-yawRef.current}deg) rotateX(${rxRef.current}deg)`;
      }
    };
    const loop = () => {
      const d = dragRef.current;
      if (!d.active) {
        d.idle += 1;
        if (d.idle > 100) yawRef.current += 0.06;
      }
      apply();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  const onDown = (e) => {
    dragRef.current = { active: true, x: e.clientX, y: e.clientY, idle: 0 };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onMove = (e) => {
    const d = dragRef.current;
    if (!d.active) return;
    yawRef.current += (e.clientX - d.x) * 0.4;
    rxRef.current = Math.min(-18, Math.max(-85, rxRef.current + (e.clientY - d.y) * 0.3));
    d.x = e.clientX;
    d.y = e.clientY;
    d.idle = 0;
  };
  const onUp = (e) => {
    dragRef.current.active = false;
    dragRef.current.idle = -240;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  const tiltRad = (tilt * Math.PI) / 180;
  const F = PANEL_LEN * Math.cos(tiltRad);
  const legRear = Math.max(8, PANEL_LEN * Math.sin(tiltRad));
  const shadowLen = Math.max(14, shadowFt * PX_PER_FT);
  const zBase = 60;
  const zRear = zBase - F;
  const zShadowMid = zBase + shadowLen / 2;
  const x0 = -((N * PANEL_W + (N - 1) * GAP) / 2) + PANEL_W / 2;
  const panels = Array.from({ length: N }, (_, i) => x0 + i * (PANEL_W + GAP));

  return (
    <div className="rounded-2xl border border-white/10 bg-ink/60 p-5" data-testid="designer-3d">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.18em] uppercase text-gold">
          <Move3d className="h-3.5 w-3.5" /> 3D row preview
        </p>
        <span className="font-mono text-[9px] tracking-[0.15em] text-mutegray">DRAG TO WALK AROUND</span>
      </div>

      <div
        className="relative mt-3 h-[340px] cursor-grab touch-none select-none overflow-hidden rounded-xl border border-gold/15 bg-gradient-to-b from-[#12151c] to-[#0A0C10] active:cursor-grabbing"
        style={{ perspective: "1000px", perspectiveOrigin: "50% 30%" }}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        data-testid="designer-3d-stage"
      >
        <div ref={worldRef} className="absolute left-1/2 top-1/2" style={{ transformStyle: "preserve-3d" }}>
          {/* ground */}
          <div
            className="absolute"
            style={{
              width: 560,
              height: 560,
              left: -280,
              top: -280,
              transform: "rotateX(90deg)",
              transformOrigin: "center",
              backgroundColor: "#10141b",
              backgroundImage:
                "linear-gradient(rgba(245,158,11,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(245,158,11,0.08) 1px, transparent 1px)",
              backgroundSize: "40px 40px",
              border: "1px solid rgba(245,158,11,0.3)",
            }}
          />

          {/* shadows (flat on ground, in front of panels) */}
          {panels.map((x, i) => (
            <div
              key={`s${i}`}
              className="absolute"
              style={{
                width: PANEL_W,
                height: shadowLen,
                left: x - PANEL_W / 2,
                top: -shadowLen / 2,
                transform: `translateZ(${zShadowMid}px) rotateX(90deg) translateZ(0.4px)`,
                background: "rgba(220,60,60,0.32)",
                border: "0.5px solid rgba(220,60,60,0.5)",
              }}
            />
          ))}

          {/* panels + legs */}
          {panels.map((x, i) => (
            <Fragment key={`p${i}`}>
              <div
                className="absolute"
                style={{
                  width: PANEL_W,
                  height: PANEL_LEN,
                  left: x - PANEL_W / 2,
                  top: -PANEL_LEN,
                  transformOrigin: "50% 100%",
                  transform: `translateZ(${zBase}px) rotateX(${90 - tilt}deg)`,
                  backgroundImage:
                    "linear-gradient(rgba(245,158,11,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(245,158,11,0.15) 1px, transparent 1px), linear-gradient(135deg, #26394e 0%, #0D1219 78%)",
                  backgroundSize: "15px 11px, 15px 11px, 100% 100%",
                  border: "1px solid rgba(255,184,0,0.6)",
                  borderRadius: 3,
                  boxShadow: "0 0 20px rgba(245,158,11,0.14)",
                }}
              />
              <div
                className="absolute"
                style={{ width: 4, height: 6, left: x - 2, top: -6, transform: `translateZ(${zBase}px)`, background: "#8B93A3" }}
              />
              <div
                className="absolute"
                style={{ width: 4, height: legRear, left: x - 2, top: -legRear, transform: `translateZ(${zRear + 1}px)`, background: "#8B93A3" }}
              />
            </Fragment>
          ))}

          {/* sun billboard (behind the row, high) */}
          <div
            ref={sunRef}
            className="absolute flex items-center justify-center rounded-full"
            style={{
              width: 58,
              height: 58,
              left: 0,
              top: 0,
              background: "radial-gradient(circle, #FFD25E 0%, #F59E0B 45%, rgba(245,158,11,0) 75%)",
              boxShadow: "0 0 46px 20px rgba(245,158,11,0.35)",
            }}
          >
            <Sun className="h-5 w-5 text-ink" />
          </div>
        </div>

        <span className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full glass border border-white/10 px-3.5 py-1.5 font-mono text-[9px] tracking-[0.2em] text-mutegray">
          {N} × PANELS AT {tilt}° — SHADOW {shadowFt.toFixed(1)} FT
        </span>
      </div>
    </div>
  );
};

export default Row3D;
